import { cn } from '../UI/cn'
import ReactCreatableSelect from 'react-select/creatable'
import { components } from 'react-select'
import React, { forwardRef } from 'react'
import { PiCaretDown, PiX } from 'react-icons/pi'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'
import { colors } from '../../styles/tokens'
import { FittedPillRow } from '../Tables/CommonTableCells/FittedPillRow'
import { Popover, PopoverContent, PopoverTrigger } from '../UI/popover'

// DS2026 caret — Phosphor chevron, replacing react-select's built-in SVG.
const Ds2026DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <PiCaretDown size={16} color={colors['neutral-500']} />
  </components.DropdownIndicator>
)

// DS2026 chip remove — Phosphor X, replacing react-select's built-in "×".
const Ds2026MultiValueRemove = (props: any) => (
  <components.MultiValueRemove {...props}>
    <PiX size={12} />
  </components.MultiValueRemove>
)

// DS2026 clear-all (X) — Phosphor X, replacing react-select's built-in "×".
// No color prop so it inherits the clearIndicator's color/hover (set in styles).
const Ds2026ClearIndicator = (props: any) => (
  <components.ClearIndicator {...props}>
    <PiX size={16} />
  </components.ClearIndicator>
)

// "+N" overflow chip — opens a scrollable popover of the hidden chips (still
// removable). Same pattern as the DS2026 data-table cells.
const Ds2026TagOverflowChip = ({
  count,
  children,
}: {
  count: number
  children: React.ReactNode
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        // Stop react-select from focusing / opening its menu when clicking +N.
        onMouseDown={(e) => e.stopPropagation()}
        // h-[24px] + rounded-[2px] match the react-select chip's height and
        // (squarer) corner radius; tabular-nums so "+1"/"+10" stay aligned.
        className="inline-flex h-[24px] shrink-0 items-center rounded-[2px] bg-neutral-100 px-2 text-xs font-medium tabular-nums text-neutral-700 transition-colors hover:bg-neutral-200"
      >
        +{count}
      </button>
    </PopoverTrigger>
    <PopoverContent
      side="bottom"
      align="start"
      className="max-h-60 w-64 overflow-y-auto !p-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap gap-1">{children}</div>
    </PopoverContent>
  </Popover>
)

// DS2026 multi-select value container: keeps selected chips on a single line and
// collapses the overflow into a "+N" popover (via FittedPillRow) so many chips
// never blow out the control height. Only used when there are chips; otherwise
// the default container renders (placeholder + input).
const Ds2026TagValueContainer = ({ children, ...props }: any) => {
  const [values, input] = children
  if (!props.hasValue) {
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    )
  }
  const chips = React.Children.toArray(values)
  return (
    <components.ValueContainer {...props}>
      <div className="flex min-w-0 flex-1 items-center">
        <FittedPillRow
          pills={chips}
          gap={4}
          // Just enough room for the small "+N" chip — so every chip that
          // genuinely fits is shown and +N only appears when truly needed.
          chipReserve={36}
          renderOverflow={(hidden, count) => (
            <Ds2026TagOverflowChip count={count}>
              {hidden}
            </Ds2026TagOverflowChip>
          )}
        />
      </div>
      {input}
    </components.ValueContainer>
  )
}

export const ReactCreatableSelectWrapper = forwardRef<any, any>(
  (props, ref) => {
    const modalDepth = useModalDepth()
    const zIndex = React.useMemo(
      () => getDropdownZIndex(modalDepth),
      [modalDepth]
    )

    return (
      <ReactCreatableSelect
        menuPortalTarget={document.body}
        closeMenuOnScroll={false}
        blurInputOnSelect={!props.isMulti}
        captureMenuScroll={false}
        menuShouldBlockScroll={false}
        {...props}
        styles={{
          ...props.styles,
          menuPortal: (provided) => ({
            ...provided,
            zIndex,
            pointerEvents: 'all',
          }),
          control: (provided, state) => ({
            // ...provided,
            ...props.styles?.control?.(provided, state),
            display: 'flex',
            minHeight: props?.height || (props?.ds2026 ? '36px' : '33.5px'),
            // DS2026 mode matches the DsInput controls (neutral-300 border,
            // shadow-sm, brand focus ring). Legacy mode keeps the sage focus and
            // no resting shadow.
            borderColor: props?.ds2026
              ? state.isFocused
                ? colors['brand-active']
                : colors['neutral-300']
              : state.isFocused
                ? '#8EA69B'
                : '#DEE2E6',
            boxShadow: props?.ds2026
              ? state.isFocused
                ? `0 0 0 4px ${colors['brand-focus']}`
                : 'none' // no resting shadow — only on hover (below)
              : state.isFocused
                ? '2px 2px 4px 0px rgba(142, 166, 155, 0.20)'
                : 'none',
            '&:hover': {
              borderColor: props?.ds2026
                ? state.isFocused
                  ? colors['brand-active']
                  : colors['neutral-400']
                : state.isFocused
                  ? '#8EA69B'
                  : '#DEE2E6',
              ...(props?.ds2026 && !state.isFocused
                ? { boxShadow: '0 1px 2px 0 rgba(16,24,40,0.05)' }
                : {}),
            },
          }),
          placeholder: (provided, state) => ({
            ...provided,
            ...props.styles?.placeholder?.(provided, state),
            color: undefined,
          }),
          multiValue: (provided, state) => ({
            ...provided,
            borderRadius: '6px',
            ...props.styles?.multiValue?.(provided, state),
          }),
          multiValueRemove: (provided, state) => ({
            ...provided,
            borderRadius: '0px 6px 6px 0px',
            ...props.styles?.multiValueRemove?.(provided, state),
          }),
        }}
        classNames={{
          ...props.classNames,
          control: (provided) => {
            return cn(
              'form-control-select',
              {
                'form-control-select-focus': provided.isFocused,
                'opacity-50': props.isDisabled,
              },
              props.classNames?.control?.(provided) ?? ''
            )
          },
          placeholder: (provided) =>
            'form-control-select-placeholder' +
            (props.classNames?.placeholder?.(provided) ?? ''),
          // DS2026 drops the pistachio hover/selected classes (which set their
          // background with !important) so the inline neutral-100 option style
          // wins; legacy keeps them.
          option: (provided) =>
            (props?.ds2026
              ? ''
              : `${provided.isFocused ? 'hover-menu-item' : ''} ${
                  provided.isSelected ? 'selected-menu-item' : ''
                }`) + (props.classNames?.option?.(provided) ?? ''),
        }}
        components={
          props?.ds2026
            ? {
                DropdownIndicator: Ds2026DropdownIndicator,
                MultiValueRemove: Ds2026MultiValueRemove,
                ClearIndicator: Ds2026ClearIndicator,
                ...(props.isMulti
                  ? { ValueContainer: Ds2026TagValueContainer }
                  : {}),
                ...props.components,
              }
            : props.components
        }
        ref={ref}
      />
    )
  }
)

ReactCreatableSelectWrapper.displayName = 'ReactCreatableSelectWrapper'
